/**
 * LinebreedingCard — ancestors appearing more than once in a hypothetical cross.
 *
 * Shared by HypotheticalPedigreePage (in-app) and SharedPedigreePage (public
 * link). Renders nothing when there is no duplication.
 *
 * Props:
 *   linebreeding: [{ name, occurrences, positions[], generations[] }]
 *                 (the `linebreeding` field of /api/breeding/hypothetical)
 */
import React from 'react';
import { Card, CardContent, Chip, Stack, Typography } from '@mui/material';

/** Only the heaviest few are listed; the rest are summarised as a count. The
 *  full picture is the chart itself, where every duplicate is colour-coded. */
const VISIBLE_ANCESTORS = 2;

export default function LinebreedingCard({ linebreeding }) {
  if (!linebreeding || linebreeding.length === 0) return null;
  const shown = linebreeding.slice(0, VISIBLE_ANCESTORS);
  const remaining = linebreeding.length - shown.length;
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" gutterBottom>
          Linebreeding ({linebreeding.length} ancestor
          {linebreeding.length === 1 ? '' : 's'} appear more than once)
        </Typography>
        <Stack spacing={1}>
          {shown.map((d, i) => (
            <Stack direction="row" spacing={1} alignItems="center" key={i}>
              <Typography variant="body2" sx={{ minWidth: 200 }}>
                {d.name}
              </Typography>
              <Chip size="small" label={`${d.occurrences}×`} />
              {/* Screen only — the chart below already shows where each ancestor
                  sits, and the printed header has to stay short. */}
              <Typography variant="caption" color="text.secondary" className="no-print">
                positions {d.positions.join(', ')} · gen {d.generations.join(', ')}
              </Typography>
            </Stack>
          ))}
          {remaining > 0 && (
            <Typography variant="caption" color="text.secondary">
              + {remaining} more {remaining === 1 ? 'ancestor' : 'ancestors'} appearing more
              than once
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
